import { formatDistanceToNow } from "date-fns";

export function time_ago(time) {
  switch (typeof time) {
    case "number":
      break;
    case "string":
      time = +new Date(time);
      break;
    case "object":
      if (time.constructor === Date) time = time.getTime();
      break;
    default:
      time = +new Date();
  }
  const time_formats = [
    [60, "seconds", 1], // 60
    [120, "1 minute ago", "1 minute from now"], // 60*2
    [3600, "minutes", 60], // 60*60, 60
    [7200, "1 hour ago", "1 hour from now"], // 60*60*2
    [86400, "hours", 3600], // 60*60*24, 60*60
    [172800, "Yesterday", "Tomorrow"], // 60*60*24*2
    [604800, "days", 86400], // 60*60*24*7, 60*60*24
    [1209600, "Last week", "Next week"], // 60*60*24*7*4*2
    [2419200, "weeks", 604800], // 60*60*24*7*4, 60*60*24*7
    [4838400, "Last month", "Next month"], // 60*60*24*7*4*2
    [29030400, "months", 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
    [58060800, "Last year", "Next year"], // 60*60*24*7*4*12*2
    [2903040000, "years", 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
    [5806080000, "Last century", "Next century"], // 60*60*24*7*4*12*100*2
    [58060800000, "centuries", 2903040000], // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
  ];
  let seconds = (+new Date() - time) / 1000,
    token = "ago",
    list_choice = 1;

  if (seconds == 0) {
    return "Just now";
  }
  if (seconds < 0) {
    seconds = Math.abs(seconds);
    token = "from now";
    list_choice = 2;
  }
  let i = 0,
    format;
  while (format === time_formats[i++])
    if (seconds < format[0]) {
      if (typeof format[2] == "string") return format[list_choice];
      else
        return Math.floor(seconds / format[2]) + " " + format[1] + " " + token;
    }
  return time;
}

// To convert the date 2024-07-09T10:19:14.294Z to the format 20 July’24 09:23 PM
export const dateTimeFormat = (givenDate: Date) => {
  // Create a new Date object from the input string
  const date = new Date(givenDate);

  const options = {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  // Format the date
  const formattedDate = date.toLocaleString("en-IN", options);

  const [fulldate, time] = formattedDate.split("at");
  const finalFormattedDate = `${fulldate}, ${time}`;

  return finalFormattedDate;
};

//to calculate difference between two dates
export const dateDifference = (date1, date2) => {
  const diffInTime = date2.getTime() - date1.getTime();

  // Calculate the difference in days
  const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24));

  // Calculate the difference in years, months and days
  const years = date2.getFullYear() - date1.getFullYear();
  const months = date2.getMonth() - date1.getMonth();
  const days = date2.getDate() - date1.getDate();

  let totalMonths = years * 12 + months;
  let totalDays = diffInDays;

  // Adjust the total days and months if days are negative
  if (days < 0) {
    totalMonths--;
    const lastMonth = new Date(date2.getFullYear(), date2.getMonth(), 0);
    totalDays += lastMonth.getDate();
  }

  return {
    days: diffInDays,
    months: totalMonths,
    years: years,
  };
};

export const getHourDifference = (date, short = false) => {
  const d = new Date(date);
  const cd = new Date(Date.now());

  const time1 = d.getTime();
  const time2 = cd.getTime();

  const hourDifference = (time1 - time2) / (1000 * 60 * 60);

  // If time difference is negative, return 0
  if (hourDifference < 0) {
    return "-";
  }

  // If less than 1 hour, show minutes
  if (hourDifference < 1) {
    const minuteDifference = Math.floor(hourDifference * 60);
    return `${minuteDifference} ${short ? "min" : "Minutes"}`;
  }

  // If more than 48 hours, show days
  if (hourDifference > 48) {
    const dayDifference = Math.floor(hourDifference / 24);
    return `${dayDifference} ${short ? "d" : "Days"}`;
  }

  // Otherwise show hours
  return `${Math.floor(hourDifference)} ${short ? "hr" : "Hours"}`;
};

export function isMoreThanXDays(dateString: string, days: number): boolean {
  const givenDate = new Date(dateString);
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const differenceInMs = currentDate.getTime() - givenDate.getTime();

  // Convert days to milliseconds and check if the difference exceeds it
  const daysInMs = days * 24 * 60 * 60 * 1000;

  return differenceInMs > daysInMs;
}

/**
 * Converts an ISO date string to a formatted date string.
 *
 * @param isoDate - The ISO date string to be converted (e.g., "2023-06-15T00:00:00.000Z").
 * @returns A formatted date string in the format "DD Month'YY" (e.g., "15 June'23").
 *
 * @example
 * const formattedDate = isoDateToDateString("2023-06-15T00:00:00.000Z");
 * console.log(formattedDate); // Output: "15 June'23"
 */
export const isoDateToDateString = (isoDate: string): string => {
  const date = new Date(isoDate);

  const day = date.getDate();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[date.getMonth()];
  const year = `'${date.getFullYear().toString().slice(-2)}`; // Get last two digits of the year

  return `${day} ${month}${year}`;
};

/**
 * Converts an ISO date string to a formatted date string.
 *
 * @param isoDate - The ISO date string to be converted (e.g., "2023-06-15T00:00:00.000Z").
 * @returns A formatted date string in the format "DD Month'YY" (e.g., "15/6/23").
 *
 * @example
 * const formattedDate = isoDateToDateString("2023-06-15T00:00:00.000Z");
 * console.log(formattedDate); // Output: "15/6/23"
 */
export const isoDateToNumberDateString = (isoDate: string): string => {
  const date = new Date(isoDate);

  const day = date.getDate();
  const month = date.getMonth();
  const year = `${date.getFullYear().toString().slice(-2)}`; // Get last two digits of the year

  return `${day}/${month + 1}/${year}`;
};

export const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInSeconds = Math.floor(
    (now?.getTime() - targetDate?.getTime()) / 1000
  );

  if (diffInSeconds < 5) {
    return "just now";
  } else if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else {
    return formatDistanceToNow(date, { addSuffix: true });
  }
};

/**
 * Converts a given ISO time string to a string in the format "DD Month'YY HH:MM IST"
 * where IST is Indian Standard Time (UTC+5:30).
 *
 * @param isoTime - The ISO time string to be converted (e.g., "2023-06-15T00:00:00.000Z")
 * @returns A string in the format "DD Month'YY HH:MM IST" (e.g., "15 June'23 00:00 IST")
 *
 * @example
 * const istTime = convertToIST("2023-06-15T00:00:00.000Z");
 * console.log(istTime); // Output: "15 June'23 05:30 IST"
 */
export function convertToIST(isoTime: string): string {
  const date = new Date(isoTime);

  // IST is UTC+5:30
  const istOffset = 5.5 * 60; // in minutes
  const utc = date.getTime() + date.getTimezoneOffset() * 60000; // UTC time in ms
  const istDate = new Date(utc + istOffset * 60000);

  const day = istDate.getDate();
  const month = istDate.toLocaleString("default", { month: "long" });
  const year = String(istDate.getFullYear()).slice(-2);
  const hours = String(istDate.getHours()).padStart(2, "0");
  const minutes = String(istDate.getMinutes()).padStart(2, "0");

  return `${day} ${month}'${year} ${hours}:${minutes} IST`;
}
